import { ProvisionType } from "@redotech/congo-enums/provision-type";
import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

import type { Contract, Target } from "../types";

const addressSchema = z.object({
  name: z.string().nullable(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  province: z.string().nullable(),
  provinceCode: z.string().nullable(),
  country: z.string().nullable(),
  countryCode: z.string().nullable(),
  postalCode: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});

const returnProductSchema = z.object({
  productId: z.string().nullable(),
  variantId: z.string().nullable(),
  lineItemId: z.string(),
  sku: z.string().nullable(),
  productTitle: z.string(),
  variantTitle: z.string(),
  quantity: z.number().int().positive(),
  status: z.string(),
  strategy: z.string(),
  reason: z.string().nullable(),
  outOfStockDuringProcessing: z.boolean().nullable(),
  exchangeFor: z
    .object({
      // Unique id of this destination (the returned product's _id). Distinguishes
      // this line from advancedExchangeItems that share the same product/variant.
      id: z.string(),
      productId: z.string().nullable(),
      variantId: z.string().nullable(),
      title: z.string().nullable(),
      variantTitle: z.string().nullable(),
      // Tags on the product being exchanged *into*. See advancedExchangeItem.
      productTags: z.array(z.string()),
    })
    .nullable(),
});

const advancedExchangeItemSchema = z.object({
  id: z.string(),
  productId: z.string().nullable(),
  variantId: z.string(),
  sku: z.string().nullable(),
  title: z.string(),
  variantTitle: z.string(),
  quantity: z.number().int().positive(),
  attributes: z.array(z.object({ key: z.string(), value: z.string() })),
  // Tags on the product being exchanged *into*. Empty when the lookup fails —
  // the function must tolerate that rather than assume the product is untagged.
  productTags: z.array(z.string()),
  outOfStockDuringProcessing: z.boolean().nullable(),
});

const orderLineItemSchema = z.object({
  id: z.string(),
  productId: z.string().nullable(),
  variantId: z.string().nullable(),
  sku: z.string().nullable(),
  productTitle: z.string(),
  variantTitle: z.string().nullable(),
  quantity: z.number().int().positive(),
  price: z.string(),
  properties: z.array(z.object({ name: z.string(), value: z.string() })),
});

const orderSchema = z.object({
  id: z.string().describe("Redo order ID."),
  shopify_id: z.string().describe("Shopify order ID."),
  name: z.string(),
  orderNumber: z.union([z.number(), z.string()]).nullable(),
  shopCurrency: z.string(),
  presentmentCurrency: z.string().nullable(),
  tags: z.array(z.string()),
  note: z.string().nullable(),
  noteAttributes: z.array(z.object({ name: z.string(), value: z.string() })),
  subtotalPrice: z.string(),
  totalPrice: z.string(),
  customer: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
      email: z.string().nullable(),
      tags: z.array(z.string()),
    })
    .nullable(),
  shippingAddress: addressSchema.nullable(),
  billingAddress: addressSchema.nullable(),
  lineItems: z.array(orderLineItemSchema),
});

const returnTotalsSchema = z.object({
  returnValue: z.number(),
  storeCredit: z.number(),
  refund: z.number(),
  newOrderValue: z.number(),
  outstandingBalance: z.number(),
});

const stripeUpsellPaymentSchema = z.object({
  paymentIntentId: z.string(),
  chargeId: z.string().nullable(),
  amount: z.number().nonnegative(),
  currency: z.string(),
});

const returnSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  type: createEnum(ReturnTypeEnum, { enumName: "ReturnType" }),
  status: createEnum(ReturnStatus, { enumName: "ReturnStatus" }),
  provision: createEnum(ProvisionType, {
    enumName: "ProvisionType",
  }).nullable(),
  shopCurrency: z.string().nullable(),
  presentmentCurrency: z.string().nullable(),
  tags: z.array(z.string()),
  shippingAddress: addressSchema.nullable(),
  products: z.array(returnProductSchema),
  advancedExchangeItems: z.array(advancedExchangeItemSchema),
  stripeUpsellPayment: stripeUpsellPaymentSchema.nullable(),
  totals: returnTotalsSchema,
});

export const exchangeOrderCreationInputSchema = z.object({
  return: returnSchema,
  order: orderSchema,
});
export type ExchangeOrderCreationInput = z.infer<
  typeof exchangeOrderCreationInputSchema
>;

const addTagsSchema = z.object({ tags: z.array(z.string().min(1)).min(1) });

const appendOrderNoteSchema = z.object({ note: z.string().min(1) });

const addOrderNoteAttributesSchema = z.object({
  attributes: z
    .array(z.object({ name: z.string().min(1), value: z.string() }))
    .min(1),
});

/**
 * Keyed by unique item id (advancedExchangeItems.id or products[].exchangeFor.id)
 * so two destinations that share a product/variant stay distinguishable.
 */
const setLineItemPropertiesSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().min(1),
        properties: z
          .array(z.object({ name: z.string().min(1), value: z.string() }))
          .min(1),
      }),
    )
    .min(1),
});

export const exchangeOrderCreationOutputSchema = z.object({
  addTags: addTagsSchema.optional(),
  appendOrderNote: appendOrderNoteSchema.optional(),
  addOrderNoteAttributes: addOrderNoteAttributesSchema.optional(),
  setLineItemProperties: setLineItemPropertiesSchema.optional(),
});
export type ExchangeOrderCreationOutput = z.infer<
  typeof exchangeOrderCreationOutputSchema
>;

export const exchangeOrderCreationTarget = {
  id: "returns.exchange-order.creation",
  displayName: "Exchange order creation",
  description:
    "Customizes tags, notes, note attributes, and line item properties when Redo creates a Shopify exchange order or draft order.",
} as const satisfies Target;

export const exchangeOrderCreationStarterCode = `// Edit this function. It receives the return and its original order.
// Return any of: addTags, appendOrderNote, addOrderNoteAttributes, setLineItemProperties.
export default async (input: Input): Promise<Output> => {
  return {};
};
`;

export const exchangeOrderCreationContractV1: Contract = {
  id: `${exchangeOrderCreationTarget.id}@2026-08.1`,
  targetId: exchangeOrderCreationTarget.id,
  inputSchema: exchangeOrderCreationInputSchema,
  outputSchema: exchangeOrderCreationOutputSchema,
  tools: [],
  sampleInput: {
    return: {
      id: "return_123",
      externalId: "RMA-1001",
      type: ReturnTypeEnum.RETURN,
      status: ReturnStatus.OPEN,
      provision: ProvisionType.Instant,
      shopCurrency: "USD",
      presentmentCurrency: "USD",
      tags: ["vip-return"],
      shippingAddress: {
        name: "Jane Doe",
        address1: "123 Main St",
        address2: null,
        city: "San Francisco",
        province: "California",
        provinceCode: "CA",
        country: "United States",
        countryCode: "US",
        postalCode: "94110",
        email: "jane@example.com",
        phone: null,
      },
      products: [
        {
          productId: "product_1",
          variantId: "variant_1",
          lineItemId: "line_item_1",
          sku: "TEE-BLK-M",
          productTitle: "Classic Tee",
          variantTitle: "Black / Medium",
          quantity: 1,
          status: "open",
          strategy: "exchange",
          reason: "Too small",
          outOfStockDuringProcessing: false,
          exchangeFor: {
            id: "exchange_for_1",
            productId: "product_1",
            variantId: "variant_2",
            title: "Classic Tee",
            variantTitle: "Black / Large",
            productTags: [],
          },
        },
      ],
      advancedExchangeItems: [],
      stripeUpsellPayment: {
        paymentIntentId: "pi_123",
        chargeId: "ch_123",
        amount: 12.5,
        currency: "USD",
      },
      totals: {
        returnValue: 40,
        storeCredit: 0,
        refund: 0,
        newOrderValue: 52.5,
        outstandingBalance: 12.5,
      },
    },
    order: {
      id: "order_123",
      shopify_id: "shopify_order_123",
      name: "#1001",
      orderNumber: 1001,
      shopCurrency: "USD",
      presentmentCurrency: "USD",
      tags: ["vip"],
      note: "Leave at the front door.",
      noteAttributes: [{ name: "gift", value: "false" }],
      subtotalPrice: "40.00",
      totalPrice: "43.20",
      customer: {
        id: "customer_123",
        name: "Jane Doe",
        email: "jane@example.com",
        tags: ["repeat-buyer"],
      },
      shippingAddress: {
        name: "Jane Doe",
        address1: "123 Main St",
        address2: null,
        city: "San Francisco",
        province: "California",
        provinceCode: "CA",
        country: "United States",
        countryCode: "US",
        postalCode: "94110",
        email: "jane@example.com",
        phone: null,
      },
      billingAddress: null,
      lineItems: [
        {
          id: "line_item_1",
          productId: "product_1",
          variantId: "variant_1",
          sku: "TEE-BLK-M",
          productTitle: "Classic Tee",
          variantTitle: "Black / Medium",
          quantity: 1,
          price: "40.00",
          properties: [],
        },
      ],
    },
  } satisfies ExchangeOrderCreationInput,
};
